import { useCallback, useEffect } from "react";

/**
 * Universal Back button — always fixed at the top-left of a page.
 * Uses browser history if available; falls back to `/` (home).
 *
 * Props:
 *  - label: string (default "Back")
 *  - fallback: string route to use when there's no history (default "/")
 *  - variant: "fixed" (default, floats over content) | "inline"
 */
export default function BackButton({ label = "[ < BACK ]", fallback = "/", variant = "fixed" }) {
  const goBack = useCallback(() => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = fallback;
    }
  }, [fallback]);

  useEffect(() => {
    window.goBack = goBack;
    window.tbhGoBack = goBack;
    return () => {
      if (window.goBack === goBack) delete window.goBack;
      if (window.tbhGoBack === goBack) delete window.tbhGoBack;
    };
  }, [goBack]);

  const base =
    "group inline-flex items-center justify-center font-mono-ui text-[10px] md:text-[12px] tracking-[0.24em] uppercase text-white hover:text-white transition-colors duration-300 px-3 py-2 border border-white/20 hover:border-white/70 bg-black/85 backdrop-blur-sm whitespace-nowrap";

  const positionClass =
    variant === "fixed"
      ? "fixed top-28 left-4 md:top-32 md:left-8 z-[240]"
      : "";

  return (
    <button
      type="button"
      onClick={goBack}
      data-testid="back-button"
      className={`nav-back-button nav-back-button--visible ${positionClass} ${base}`}
      aria-label="Go back"
    >
      {label}
    </button>
  );
}
